// ⚠️ MUST stay the first import: modules below read localStorage while they load.
import './lib/storageFallback';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppUpdatePrompt } from './components/AppUpdatePrompt';
import { startAppUpdateWatcher } from './lib/appUpdate';
import { startDayLogFocusRefresh } from './lib/dayLogFocusRefresh';
import { startBlobFocusRefresh } from './lib/blobFocusRefresh';
import { useTheme } from './hooks/useTheme';
import { initSentry } from './lib/sentry';
import './index.css';
import './styles/theme.css';

// Initialize error tracking as early as possible (no-op without a DSN).
initSentry();

// Initialize theme
const initializeTheme = () => {
  try {
    const themeState = useTheme.getState();
    const theme = themeState?.theme || 'light';
    const primaryColor = themeState?.primaryColor || '#2563eb';

    document.documentElement.classList.toggle('dark', theme === 'dark');

    const isMetanoia = primaryColor === 'metanoia';
    document.documentElement.classList.toggle('theme-metanoia', isMetanoia);

    const hexToRGB = (hex: string) => {
      try {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) {
          return '37 99 235';
        }
        return `${r} ${g} ${b}`;
      } catch {
        return '37 99 235';
      }
    };

    if (isMetanoia) {
      // Solid accent = the calm cyan brand stop (NOT the purple mid). Purple no
      // longer leaks through the token; the full spectral gradient lives on
      // --primary-gradient for fills, selection borders and signature moments.
      document.documentElement.style.setProperty('--primary', hexToRGB('#3CC2DD'));
      document.documentElement.style.setProperty('--primary-gradient', 'linear-gradient(135deg, #3CC2DD, #A739E2, #F58C56)');
    } else {
      document.documentElement.style.setProperty('--primary', hexToRGB(primaryColor));
      document.documentElement.style.setProperty('--primary-gradient', 'none');
    }
    return true;
  } catch (error) {
    console.warn('Theme initialization failed, using defaults:', error);
    document.documentElement.style.setProperty('--primary', '37 99 235');
    return true;
  }
};

// Mount application with error boundary
const mount = () => {
  const rootElement = document.getElementById('root');

  if (!rootElement) {
    console.error('Root element not found');
    return;
  }

  // Initialize theme
  initializeTheme();

  // Set up a global timeout to detect if app fails to render
  const mountTimeout = setTimeout(() => {
    if (document.hidden) return;
    const hasRendered = rootElement.querySelector('[class*="min-h-screen"]');
    if (!hasRendered) {
      console.error('App failed to mount properly');
    }
  }, 15000);

  try {
    const root = createRoot(rootElement);

    root.render(
      <StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
        {/* ⚠️ OUTSIDE the boundary: it must still show when the app below has failed. */}
        <AppUpdatePrompt />
      </StrictMode>
    );

    clearTimeout(mountTimeout);

    // A deploy under an open tab → ask to refresh before anything breaks (lib/appUpdate.ts).
    startAppUpdateWatcher();

    // A tab that comes back re-reads TODAY's day logs (drinks, food, ticks, cycle day) —
    // read only, throttled (lib/dayLogFocusRefresh.ts).
    startDayLogFocusRefresh();

    // A tab that comes back catches up with the synced docs (identity and its AI
    // visibility, coach, AI chat, …) — throttled, a timestamp unless the row moved
    // (lib/blobFocusRefresh.ts).
    startBlobFocusRefresh();

    // Fade out the instant brand splash once React has painted. App renders its own
    // matching <BrandLoader> while it finishes loading, so the hand-off is seamless
    // (and there's never a white flash — html/body background is already dark).
    const splash = document.getElementById('app-loading');
    if (splash) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          splash.classList.add('ld-hide');
          setTimeout(() => splash.remove(), 500);
        })
      );
    }
  } catch (error) {
    console.error('Critical mount error:', error);
    clearTimeout(mountTimeout);
    rootElement.innerHTML = `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #0a0a0a; color: #ffffff; font-family: system-ui; padding: 20px;">
        <div style="max-width: 500px; text-align: center;">
          <h1 style="color: #ef4444; margin-bottom: 16px;">Failed to Start</h1>
          <p style="color: #999; margin-bottom: 24px;">The application failed to initialize. Please try refreshing the page.</p>
          <button onclick="localStorage.clear(); window.location.reload();" style="padding: 12px 24px; background: #2563eb; color: white; border: none; border-radius: 6px; cursor: pointer;">
            Clear Data and Reload
          </button>
        </div>
      </div>
    `;
  }
};

// ⚠️ DEV ONLY: an address from `npm run dev:login` carries a one-time sign-in token — redeem
// it and reload instead of mounting (lib/devSession.ts). `import.meta.env.DEV` is `false` in
// a build, so this branch and its chunk are dropped from production.
if (import.meta.env.DEV && /[#&]dev_login=/.test(window.location.hash)) {
  void import('./lib/devSession').then(m => m.applyDevSession());
} else if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mount);
} else {
  mount();
}